import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, ShieldCheck, Layers, Lightbulb, CheckCircle2 } from 'lucide-react';
import Card from '../ui/Card';

const corePillars = [
  {
    icon: <Cpu className="w-6 h-6 text-brand-600 dark:text-brand-400" />,
    title: 'AI & Intelligent Systems',
    description: 'Developing tailored machine learning models, retrieval-augmented intelligence, and automated decision-making engines designed for real-world operational environments.'
  },
  {
    icon: <ShieldCheck className="w-6 h-6 text-indigoBrand-600 dark:text-indigoBrand-400" />,
    title: 'Cybersecurity',
    description: 'Architecting zero-trust perimeter defenses, behavioral intrusion detection, and proactive vulnerability analytics to safeguard critical infrastructure and sensitive assets.'
  },
  {
    icon: <Layers className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />,
    title: 'Product Engineering',
    description: 'Delivering end-to-end software engineering that turns architectural blueprints into resilient, high-availability cloud, web, and distributed applications.'
  },
  {
    icon: <Lightbulb className="w-6 h-6 text-amber-600 dark:text-amber-400" />,
    title: 'Technology Innovation',
    description: 'Researching and deploying emerging technologies across media authenticity forensics, intelligent mobility telematics, and digital healthcare connectivity.'
  }
];

const About = () => {
  return (
    <section id="about" className="py-24 relative bg-slate-50/60 dark:bg-slate-900/40 border-y border-slate-200/80 dark:border-slate-800">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4"
          >
            About The Company
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4"
          >
            Technology With Purpose
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed"
          >
            Mira Future Tech Vision Pvt. Ltd. is an Indian technology company focused on research-driven product engineering. We build practical software platforms that solve acute problems in safety, healthcare, education, and enterprise operations.
          </motion.p>
        </div>

        {/* 4 Professional Pillar Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {corePillars.map((pillar, idx) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
            >
              <Card className="h-full border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-subtle hover:shadow-card hover:border-brand-500/40 p-7">
                <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center mb-6">
                  {pillar.icon}
                </div>
                <h3 className="text-lg font-bold font-display text-slate-900 dark:text-white mb-3">
                  {pillar.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Subtle Company Values Bar */}
        <div className="mt-16 pt-10 border-t border-slate-200 dark:border-slate-800 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">Architectural Rigor</h4>
              <p className="text-xs text-slate-500 leading-relaxed">Engineered using modular microservices and verifiable security protocols.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-indigoBrand-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">Transparent Engineering</h4>
              <p className="text-xs text-slate-500 leading-relaxed">Clear software lifecycles, documented APIs, and honest benchmarks.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">Impact-Driven Deliverables</h4>
              <p className="text-xs text-slate-500 leading-relaxed">Solving genuine friction points for organizations, healthcare providers, and users.</p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;
